import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  LayoutDashboard,
  Receipt,
  TrendingUp,
  CreditCard,
  Bot,
  Briefcase,
  Newspaper,
  Bell,
  User,
  LogOut,
  Settings,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { aiAPI } from '../services/apiServices';
import SidebarProfile from './SidebarProfile';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import Logo from './Logo';

// SidebarContent moved outside to prevent unmounting when parent (DashboardLayout) re-renders
const SidebarContent = ({ user, navItems, handleLogout, setOpen }) => (
  <div className="flex flex-col h-full bg-[#020617] border-r border-white/5">
    <div className="p-4 border-b border-white/5">
      <SidebarProfile user={user} />
    </div>

    <nav className="flex-1 p-4 overflow-y-auto custom-scrollbar">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = window.location.pathname === item.path;
        let activeColorClass = 'bg-emerald-600';
        let shadowClass = 'shadow-emerald-500/20';

        if (item.path === '/dashboard') {
          activeColorClass = 'bg-orange-500';
          shadowClass = 'shadow-orange-500/30';
        }

        if (item.path === '/expenses') {
          activeColorClass = 'bg-emerald-500';
          shadowClass = 'shadow-emerald-500/30';
        }
        if (item.path === '/loans') {
          activeColorClass = 'bg-gradient-to-r from-[#16A34A] to-[#84CC16]';
          shadowClass = 'shadow-green-500/30';
        }
        if (item.path === '/investments') {
          activeColorClass = 'bg-gradient-to-r from-[#0284C7] to-[#22D3EE]';
          shadowClass = 'shadow-cyan-500/30';
        }
        if (item.path === '/ai-tools') {
          activeColorClass = 'bg-gradient-to-r from-[#FB7185] to-[#E11D48]';
          shadowClass = 'shadow-rose-500/30';
        }
        if (item.path === '/portfolio') {
          activeColorClass = 'bg-gradient-to-r from-[#6366f1] to-[#a855f7]';
          shadowClass = 'shadow-indigo-500/30';
        }
        if (item.path === '/consultants') {
          activeColorClass = 'bg-gradient-to-r from-[#E6A082] to-[#CC896E]';
          shadowClass = 'shadow-[#E6A082]/20';
        }
        if (item.path === '/news') {
          activeColorClass = 'bg-gradient-to-r from-[#F59E0B] to-[#D97706]';
          shadowClass = 'shadow-amber-500/30';
        }
        if (item.path === '/reminders') {
          activeColorClass = 'bg-slate-400';
          shadowClass = 'shadow-slate-500/10';
        }
        if (item.path === '/settings') {
          activeColorClass = 'bg-white';
          shadowClass = 'shadow-white/30';
        }

        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setOpen && setOpen(false)}
            className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 transition-all duration-200 ${isActive
              ? `${activeColorClass} text-black font-bold shadow-lg ${shadowClass}`
              : 'text-gray-300 hover:bg-slate-800 hover:text-white'
              }`}
          >
            <Icon size={20} />
            <span className="font-medium">{item.label}</span>
          </Link>
        );
      })}
    </nav>

    <div className="p-4 border-t border-white/5 mt-auto">
      <Button
        onClick={handleLogout}
        variant="ghost"
        className="w-full justify-start text-red-500/80 hover:text-red-400 hover:bg-red-500/10"
      >
        <LogOut size={20} className="mr-3" />
        Logout
      </Button>
    </div>
  </div>
);

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('dark');
    localStorage.setItem('theme', 'dark');
  }, []);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/expenses', icon: Receipt, label: 'Expenses' },
    { path: '/investments', icon: TrendingUp, label: 'Investments' },
    { path: '/loans', icon: CreditCard, label: 'Loans' },
    { path: '/ai-tools', icon: Bot, label: 'Finance Chatbot' },
    { path: '/portfolio', icon: Briefcase, label: 'Portfolio' },
    { path: '/consultants', icon: User, label: 'Financial Literacy' },
    { path: '/news', icon: Newspaper, label: 'Financial News' },
    { path: '/reminders', icon: Bell, label: 'Reminders' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('hasShownRemindersThisSession');
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      {/* Desktop Sidebar - High z-index to stay above content */}
      <aside className="hidden md:flex w-64 flex-col shrink-0 z-[50]">
        <SidebarContent user={user} navItems={navItems} handleLogout={handleLogout} />
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden relative w-full">
        {/* Mobile Header */}
        <header className="md:hidden flex items-center justify-between p-4 bg-[#020617] border-b border-white/5 z-10 sticky top-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-purple-900/20 rounded-full blur-sm"></div>
              <img
                src="/logo.jpg"
                alt="FinAssure Logo"
                className="relative w-10 h-10 object-cover rounded-full border border-purple-500/30 p-1"
                style={{
                  filter: 'brightness(1.4) contrast(1.1) drop-shadow(0 0 15px rgba(168, 85, 247, 0.8))',
                  background: 'radial-gradient(circle, rgba(168, 85, 247, 0.15), transparent 70%)',
                }}
              />
            </div>
            <span className="font-bold text-white tracking-tight">FinAssure</span>
          </div>
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6 text-gray-200" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 border-r border-white/5 bg-[#020617]">
                <SidebarContent user={user} navItems={navItems} handleLogout={handleLogout} setOpen={setOpen} />
              </SheetContent>
            </Sheet>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-0 relative bg-slate-950">
          {location.pathname === '/dashboard' && (
            <div className="hidden md:block absolute top-6 right-8 z-10 pointer-events-none opacity-80 hover:opacity-100 transition-opacity">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-purple-900/20 rounded-full blur-md"></div>
                <img
                  src="/logo.jpg"
                  alt="FinAssure Logo"
                  className="relative w-14 h-14 object-cover rounded-full border border-purple-500/30 p-1"
                  style={{
                    filter: 'brightness(1.4) contrast(1.1) drop-shadow(0 0 20px rgba(168, 85, 247, 0.9))',
                    background: 'radial-gradient(circle, rgba(168, 85, 247, 0.15), transparent 70%)',
                  }}
                />
              </div>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;